import { z } from 'zod';

/**
 * Subconta Asaas (BaaS) do colaborador do CRM — o contrato entre o CRM, as
 * Functions e o split.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200 (BaaS: subconta para colaborador +55).
 *
 * Por quê: até a #845 o colaborador só recebia comissão pelo split do Asaas se
 * o ADMIN colasse um `walletId` à mão (`crm_users.asaasWalletId`, #748/#753), e
 * quase nenhum colaborador tem conta Asaas — a comissão caía no extrato a
 * pagar. Agora o ADMIN cria a subconta pela API (`POST /v3/accounts`) e o
 * `walletId` devolvido vira a carteira do split.
 *
 * Decisões do owner (2026-10-07, registradas na #845):
 *  1. a `apiKey` da subconta é GUARDADA (Secret Manager, nunca Firestore/log);
 *  2. KYC pelo link do Asaas (`onboardingUrl` de `GET /myAccount/documents`);
 *  3. saque do colaborador liberado (ver `docs/billing/asaas.md`, BaaS);
 *  4. ligado já em produção;
 *  5. o teto de R$ 2.000 do Período de Avaliação vale também para o split
 *     recebido;
 *  6. os dados pessoais do titular ficam no cadastro do colaborador;
 *  7. encerramento da subconta é manual, no painel do Asaas.
 *
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts` (callables
 * `crmCreateAsaasSubaccount`/`crmGetAsaasSubaccount`) e
 * `lib/billing/saleSplitSnapshot.ts` (teto e modo no split); CRM
 * `features/sellers/SellerAsaasAccountPage.tsx` e `lib/callables.ts`.
 *
 * Arquitetura: fonte da verdade da subconta é o Asaas; o espelho em
 * `crm_users/{uid}.asaasSubaccount` é cache escrito só pelo servidor (rules:
 * `write: false`; leitura: o próprio, ADMIN e AUDITOR).
 *
 * Issues: #845 (esta feature), #748 (épico Asaas + split), #749 (spike A0:
 * `POST /accounts` medido), #753 (carteira do parceiro), #762 (validação de saque).
 */

/**
 * Teto de subcontas no Período de Avaliação do BaaS.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200. Fonte: central de ajuda do Asaas,
 * seção "Período de avaliação" (https://central.ajuda.asaas.com/hc/pt-br/sections/47492558286747).
 */
export const BAAS_EVALUATION_MAX_SUBACCOUNTS = 10;

/**
 * Duração do Período de Avaliação, em dias corridos, contada da 1ª subconta
 * criada via API em produção.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200 (mesma fonte acima).
 */
export const BAAS_EVALUATION_DAYS = 60;

/** `BAAS_EVALUATION_DAYS` em ms. */
export const BAAS_EVALUATION_MS = BAAS_EVALUATION_DAYS * 24 * 60 * 60 * 1000;

/**
 * Teto por subconta no Período de Avaliação: R$ 2.000,00 em centavos.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200. O Asaas soma cobranças avulsas,
 * links e assinaturas; o owner decidiu (2026-10-07, decisão 5) tratar o split
 * RECEBIDO como parte do mesmo teto.
 */
export const BAAS_EVALUATION_CAP_CENTS = 200_000;

/**
 * A partir de quanto recebido pelo split a carteira da subconta deixa de entrar
 * em vendas NOVAS durante o Período de Avaliação — 80 % do teto.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Por quê a folga de 20 %: o teto só é conferido quando o split é CONGELADO
 * (link da venda). As vendas já congeladas seguem mandando split a cada
 * cobrança recorrente, e esse dinheiro chega depois da conferência. Os 20 %
 * absorvem as recorrências que ainda vão cair dentro dos 60 dias. Número não
 * medido: é uma margem, e o log `saleSplit_baas_cap_reached` mostra quando ela
 * é atingida.
 */
export const BAAS_SPLIT_GUARD_CENTS = 160_000;

/**
 * Status do espelho da subconta no `crm_users`.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * - `CREATING`: a callable reservou a criação (trava contra duplo clique); se
 *   ficar parado, a próxima tentativa procura a subconta pelo CPF no Asaas
 *   antes de criar outra (`GET /accounts?cpfCnpj=`).
 * - `ACTIVE`: subconta criada; `wallet_id` é a carteira do split.
 * - `FAILED`: o Asaas recusou (dados inválidos, e-mail já usado…). O ADMIN
 *   corrige e tenta de novo.
 */
export const CrmAsaasSubaccountStatusSchema = z.enum(['CREATING', 'ACTIVE', 'FAILED']);
export type CrmAsaasSubaccountStatus = z.infer<typeof CrmAsaasSubaccountStatusSchema>;
/** Os status como constantes (`CrmAsaasSubaccountStatus.ACTIVE`, #761). */
export const CrmAsaasSubaccountStatus = CrmAsaasSubaccountStatusSchema.enum;

/**
 * Valida um CPF pelos dois dígitos verificadores (mod 11).
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Por quê: o `TaxIdValidator` do BR é o de CNPJ (empresa); a subconta do
 * colaborador é de pessoa física, e o Asaas devolve 400 para CPF inválido —
 * melhor recusar antes, com mensagem no campo.
 *
 * Contrato: recebe só dígitos (11); sequência repetida (`111…`) é inválida.
 *
 * Usado por: `CpfSchema` (este módulo, e com ele o input de
 * `crmCreateAsaasSubaccount` nas Functions); CRM
 * `features/sellers/SellerAsaasAccountPage.tsx` (erro no campo antes do envio).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function isValidCpf(digits: string): boolean {
  if (!/^\d{11}$/.test(digits) || /^(\d)\1{10}$/.test(digits)) return false;
  const nums = digits.split('').map(Number);
  const dv = (len: number): number => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += (nums[i] ?? 0) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return dv(9) === nums[9] && dv(10) === nums[10];
}

/**
 * Data `AAAA-MM-DD` que existe no calendário e já passou.
 *
 * Origem: #845 — auditoria 2026-10-07 19:01 +0200 (A24, BAIXO): o regex sozinho
 * aceitava `1990-13-45`, e o Asaas só recusava depois, com mensagem genérica.
 *
 * Contrato: `now` em epoch ms (default `Date.now()` — só validação de
 * formulário, nada persistido decide por ele). A data é lida como meia-noite UTC.
 *
 * Usado por: `CrmCreateAsaasSubaccountInputSchema.birthDate` (este módulo);
 * CRM `features/sellers/SellerAsaasAccountPage.tsx`.
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function isPastIsoDate(s: string, now: number = Date.now()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const ms = Date.parse(`${s}T00:00:00Z`);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === s && ms < now;
}

/**
 * CPF só com dígitos e DV válido — tira máscara (`000.000.000-00`) antes de
 * validar. Origem: #845 — 2026-10-07 18:23 +0200. Usado por:
 * `CrmCreateAsaasSubaccountInputSchema.cpf` (este módulo).
 */
export const CpfSchema = z
  .string()
  .transform((s) => s.replace(/\D/g, ''))
  .refine(isValidCpf, 'CPF inválido.');

/**
 * Dados do titular da subconta, como o ADMIN os digita no cadastro do
 * colaborador e como ficam gravados em `crm_users.asaasSubaccount.holder`.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200 (decisão 6: os dados pessoais ficam no
 * cadastro do colaborador).
 *
 * Dados — campos obrigatórios do `POST /v3/accounts` para pessoa física
 * (https://docs.asaas.com/reference/criar-subconta, lido em 2026-10-07):
 *   { "cpf": "00000000000", "birth_date": "1990-01-31",
 *     "income_cents": 300000,         // renda MENSAL, centavos (o Asaas recebe reais)
 *     "postal_code": "01310100",      // CEP; inválido → 400 do Asaas (ele deduz a cidade)
 *     "address": "Av. Paulista", "address_number": "1000", "complement": null,
 *     "province": "Bela Vista",       // bairro
 *     "email": "colaborador@example.com" }
 * O telefone NÃO entra aqui: é o `crm_users.phone` (E.164 +55), o mesmo que
 * torna o colaborador elegível.
 *
 * Usado por: `CrmAsaasSubaccountSchema.holder` (este módulo) — gravado pela
 * callable `crmCreateAsaasSubaccount` (`billing/asaas/collaboratorSubaccount.ts`).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export const CrmAsaasSubaccountHolderSchema = z.object({
  cpf: z.string().regex(/^\d{11}$/),
  birth_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  income_cents: z.number().int().positive(),
  postal_code: z.string().regex(/^\d{8}$/),
  address: z.string().min(1).max(200),
  address_number: z.string().min(1).max(20),
  complement: z.string().max(100).nullable(),
  province: z.string().min(1).max(100),
  email: z.email(),
});
export type CrmAsaasSubaccountHolder = z.infer<typeof CrmAsaasSubaccountHolderSchema>;

/**
 * Espelho da subconta em `crm_users/{uid}.asaasSubaccount`.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Dados — exemplo anonimizado (subconta criada em produção):
 *   { "status": "ACTIVE", "livemode": true,
 *     "account_id": "00000000-0000-4000-8000-000000000001",
 *     "wallet_id": "00000000-0000-4000-8000-000000000002",
 *     "api_key_secret_version": "7",   // versão em ASAAS_SUBACCOUNT_API_KEYS; null = chave não guardada
 *     "requested_at": 1791000000000, "created_at": 1791000003000,
 *     "created_by": "<uid do ADMIN>", "error": null,
 *     "holder": { …CrmAsaasSubaccountHolderSchema… } }
 *
 * Armadilhas:
 *  - `livemode` decide em quais vendas a carteira vale: uma carteira de
 *    produção numa venda do sandbox (ou o contrário) é recusada pelo Asaas
 *    ("Wallet inexistente", #749) — `subaccountWalletUsable` a tira do split.
 *  - `api_key_secret_version: null` com `status: ACTIVE` = subconta ADOTADA
 *    (achada pelo CPF depois de um timeout) ou chave perdida na gravação: a
 *    `apiKey` só vem na criação. Sem ela não há link de documentos pela API.
 *  - Leitura TOLERANTE no `CrmUserSchema` (`.catch(null)`): `getCrmUser` faz
 *    safeParse do doc inteiro, e um espelho malformado não pode trancar o
 *    colaborador fora do CRM (mesmo motivo do `asaasWalletId`, #748).
 *
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts` (escreve e
 * lê) e `lib/billing/saleSplitSnapshot.ts` (via `subaccountWalletUsable`);
 * `packages/types` `crm.ts` (`CrmUserSchema.asaasSubaccount`); CRM
 * `SellerAsaasAccountPage.tsx` (via `CrmAsaasSubaccountViewSchema`).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export const CrmAsaasSubaccountSchema = z.object({
  status: CrmAsaasSubaccountStatusSchema,
  livemode: z.boolean(),
  account_id: z.string().min(1).max(128).nullable(),
  wallet_id: z.string().min(1).max(128).nullable(),
  api_key_secret_version: z.string().min(1).max(32).nullable(),
  requested_at: z.number().int().nonnegative(),
  created_at: z.number().int().nonnegative().nullable(),
  created_by: z.string().min(1).max(128),
  /** Motivo da última falha (texto do Asaas, cortado). `null` fora de `FAILED`. */
  error: z.string().max(300).nullable(),
  holder: CrmAsaasSubaccountHolderSchema,
});
export type CrmAsaasSubaccount = z.infer<typeof CrmAsaasSubaccountSchema>;

/**
 * Input de `crmCreateAsaasSubaccount` (callable das Functions, só ADMIN do CRM).
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * `livemode` existe para o ensaio no sandbox (bateria local); a tela do CRM
 * sempre manda `true` (decisão 4 do owner: produção já). `incomeValueCents` em
 * centavos, convertido para reais só na borda do Asaas.
 *
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts` (parse do
 * input); CRM `lib/callables.ts` e `features/sellers/SellerAsaasAccountPage.tsx`
 * (validação do formulário antes do envio).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export const CrmCreateAsaasSubaccountInputSchema = z.object({
  uid: z.string().min(1).max(128),
  cpf: CpfSchema,
  birthDate: z.string().refine((s) => isPastIsoDate(s), 'Data AAAA-MM-DD válida e no passado.'),
  incomeValueCents: z.number().int().positive().max(10_000_000_000),
  postalCode: z
    .string()
    .transform((s) => s.replace(/\D/g, ''))
    .refine((s) => /^\d{8}$/.test(s), 'CEP com 8 dígitos.'),
  address: z.string().trim().min(1).max(200),
  addressNumber: z.string().trim().min(1).max(20),
  complement: z.string().trim().max(100).nullable().optional(),
  province: z.string().trim().min(1).max(100),
  email: z.email(),
  livemode: z.boolean().default(true),
});
export type CrmCreateAsaasSubaccountInput = z.infer<typeof CrmCreateAsaasSubaccountInputSchema>;

/**
 * Input de `crmGetAsaasSubaccount`. Origem: #845 — 2026-10-07 18:23 +0200.
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts`; CRM
 * `lib/callables.ts`.
 */
export const CrmGetAsaasSubaccountInputSchema = z.object({ uid: z.string().min(1).max(128) });
export type CrmGetAsaasSubaccountInput = z.infer<typeof CrmGetAsaasSubaccountInputSchema>;

/**
 * Por que o colaborador não pode ter subconta criada.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 * `PHONE_NOT_BR`: telefone ausente ou de outro país (a regra da #845 é +55,
 * resolvido por libphonenumber, nunca por prefixo de texto). `PARTNER_USER`:
 * usuário de parceiro recebe pela carteira do PARCEIRO (#753), não pela dele.
 * `REMOVED`: colaborador removido.
 *
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts` (decide);
 * `CrmAsaasSubaccountViewSchema.ineligible` (CRM mostra o motivo).
 */
export const CrmAsaasSubaccountIneligibleSchema = z.enum(['PHONE_NOT_BR', 'PARTNER_USER', 'REMOVED']);
export type CrmAsaasSubaccountIneligible = z.infer<typeof CrmAsaasSubaccountIneligibleSchema>;
export const CrmAsaasSubaccountIneligible = CrmAsaasSubaccountIneligibleSchema.enum;

/**
 * Situação de cadastro do Asaas (`GET /myAccount/status`). Valor novo → `UNKNOWN`
 * (`.catch`, não `.default`: tolera o valor que esta versão não conhece).
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Usado por: Functions `billing/providers/asaasSchemas.ts` (resposta do Asaas)
 * e `billing/asaas/collaboratorSubaccount.ts`; `CrmAsaasSubaccountViewSchema`.
 */
export const AsaasAccountReviewStatusSchema = z
  .enum(['PENDING', 'APPROVED', 'REJECTED', 'AWAITING_APPROVAL', 'UNKNOWN'])
  .catch('UNKNOWN');
export type AsaasAccountReviewStatus = z.infer<typeof AsaasAccountReviewStatusSchema>;

/**
 * O que a tela "Conta Asaas" do colaborador mostra — saída de
 * `crmGetAsaasSubaccount`.
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Contrato: `onboarding` é `null` quando não há `apiKey` guardada, quando a
 * subconta não está `ACTIVE` ou quando o Asaas não respondeu (`onboardingError`
 * diz qual). Os links (`onboarding_url`) expiram — a tela relê a cada abertura.
 *
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts` (monta);
 * CRM `features/sellers/SellerAsaasAccountPage.tsx` (lê).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export const CrmAsaasSubaccountViewSchema = z.object({
  uid: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  ineligible: CrmAsaasSubaccountIneligibleSchema.nullable(),
  /** Pré-preenchimento do formulário a partir do perfil (`taxId`, `birthDate`, e-mail). */
  prefill: z.object({
    cpf: z.string().nullable(),
    birthDate: z.string().nullable(),
    email: z.string().nullable(),
  }),
  subaccount: CrmAsaasSubaccountSchema.nullable(),
  /** A carteira do split hoje é a desta subconta? (o ADMIN pode ter colado outra.) */
  walletInUse: z.boolean(),
  onboarding: z
    .object({
      general: AsaasAccountReviewStatusSchema,
      documentation: AsaasAccountReviewStatusSchema,
      documents: z.array(
        z.object({
          title: z.string(),
          status: z.string(),
          onboarding_url: z.string().nullable(),
          onboarding_url_expires: z.string().nullable(),
        }),
      ),
    })
    .nullable(),
  onboardingError: z.enum(['NO_API_KEY', 'ASAAS_UNAVAILABLE']).nullable(),
  /** Período de Avaliação (só produção): início, subcontas usadas e o teto. */
  evaluation: z
    .object({
      started_at: z.number().int().nullable(),
      used: z.number().int().nonnegative(),
      max: z.number().int().positive(),
    })
    .nullable(),
});
export type CrmAsaasSubaccountView = z.infer<typeof CrmAsaasSubaccountViewSchema>;

/**
 * A subconta ainda está no Período de Avaliação?
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Contrato: `startedAt` nulo (nenhuma subconta criada em produção) → `false`.
 * O sandbox não tem Período de Avaliação: quem chama passa `null` lá. `now`
 * em epoch ms, passado pelo chamador.
 *
 * Usado por: `subaccountWalletUsable` (este módulo); Functions
 * `billing/asaas/collaboratorSubaccount.ts` (bloco `evaluation` da tela).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function isBaasEvaluationActive(startedAt: number | null | undefined, now: number): boolean {
  return typeof startedAt === 'number' && now - startedAt < BAAS_EVALUATION_MS;
}

/**
 * A carteira do colaborador pode entrar no split desta venda?
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Por quê: três casos tiram a carteira de uma subconta NOSSA do split (a
 * comissão cai no extrato a pagar — `MANUAL` —, a venda nunca é bloqueada):
 *  1. modo diferente do da venda — carteira de produção numa venda do sandbox
 *     (ou o contrário) é recusada pelo Asaas, e a `/pay` não cobraria;
 *  2. subconta não `ACTIVE`;
 *  3. Período de Avaliação ativo e o recebido pelo split já passou de
 *     `BAAS_SPLIT_GUARD_CENTS` (decisão 5 do owner).
 * Carteira colada à mão (sem espelho, ou diferente da `wallet_id` da
 * subconta) segue a regra de antes: vale.
 *
 * Contrato: puro. `saleLivemode` ausente = venda do Stripe ou chamador antigo
 * — sem conferência de modo. `receivedCents` é o que a subconta já recebeu
 * pelo split desde que nasceu (`crm_commissions` `ASAAS_SPLIT` `PAID`).
 *
 * Usado por: `readCollaborator` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`).
 */
export function subaccountWalletUsable(args: {
  readonly walletId: string | null | undefined;
  readonly subaccount: CrmAsaasSubaccount | null | undefined;
  readonly saleLivemode: boolean | undefined;
  readonly evaluationStartedAt: number | null;
  readonly receivedCents: number;
  readonly now: number;
}): { usable: true } | { usable: false; reason: 'MODE_MISMATCH' | 'NOT_ACTIVE' | 'EVALUATION_CAP' } {
  const sub = args.subaccount;
  const wallet = args.walletId?.trim().toLowerCase();
  if (!sub || !wallet || sub.wallet_id?.toLowerCase() !== wallet) return { usable: true };
  if (sub.status !== CrmAsaasSubaccountStatus.ACTIVE) return { usable: false, reason: 'NOT_ACTIVE' };
  if (args.saleLivemode !== undefined && sub.livemode !== args.saleLivemode) {
    return { usable: false, reason: 'MODE_MISMATCH' };
  }
  if (sub.livemode && isBaasEvaluationActive(args.evaluationStartedAt, args.now) && args.receivedCents >= BAAS_SPLIT_GUARD_CENTS) {
    return { usable: false, reason: 'EVALUATION_CAP' };
  }
  return { usable: true };
}

/**
 * Estado global do BaaS na conta pai — `config/asaasBaas` (só Admin SDK).
 *
 * Origem: #845 — 2026-10-07 18:23 +0200.
 *
 * Dados — exemplo:
 *   { "evaluation_started_at": 1791000003000,   // 1ª subconta criada em PRODUÇÃO
 *     "live_subaccounts_created": 3,            // contador nosso (o Asaas é a verdade: GET /accounts)
 *     "updated_at": 1791000003000 }
 * Só produção conta: o sandbox não tem Período de Avaliação. Campos com
 * `.catch` — doc ausente ou malformado lê como "nenhuma subconta ainda".
 *
 * Usado por: Functions `billing/asaas/collaboratorSubaccount.ts` (escreve na
 * criação) e `lib/billing/saleSplitSnapshot.ts` (lê o início da avaliação).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export const AsaasBaasConfigSchema = z.object({
  evaluation_started_at: z.number().int().nonnegative().nullable().catch(null),
  live_subaccounts_created: z.number().int().nonnegative().catch(0),
  updated_at: z.number().int().nonnegative().nullable().catch(null),
});
export type AsaasBaasConfig = z.infer<typeof AsaasBaasConfigSchema>;

/** Caminho do doc de estado do BaaS. */
export const ASAAS_BAAS_CONFIG_PATH = 'config/asaasBaas';
